import React, { useState, useRef } from "react";
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Divider,
  CloseButton,
  FieldWrapper,
  Input,
  ErrorMessage,
  ButtonWrapper,
} from "./ModuleModal.styles";
import { ModuleModalProps, ModuleSchemaField } from "./ModuleModal.types";
import Button from "../../Buttons/Button";
import DropdownWithInput from "../../Dropdowns/DropdownWithInput";
import DatePickerInput from "../../CalendarInput/CalendarInput"; // Добавляем импорт для DatePickerInput
import closeIcon from "../../../assets/icons/closeIcon.svg";
import TitleTooltip from "../../TitleTooltip/TitleTooltip";

const ModuleModal: React.FC<ModuleModalProps> = ({
  title,
  fields,
  onSubmit,
  onCancel,
  submitButtonText = "Сохранить",
  initialValues = {},
}) => {
  const [formData, setFormData] = useState<Record<string, string>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [currentGroupIndex, setCurrentGroupIndex] = useState<number>(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Собираем уникальные группы
  const groups = Array.from(
    new Set(fields.map((f) => f.group).filter((g) => g != null))
  ) as number[];

  groups.sort((a, b) => a - b);

  const noGroupFields = fields.filter((f) => !f.group);
  let extendedGroups = groups;
  if (noGroupFields.length > 0) {
    extendedGroups = [-1, ...groups];
  }

  const currentGroupId = extendedGroups[currentGroupIndex] ?? -1;
  const currentGroupFields =
    currentGroupId === -1
      ? noGroupFields
      : fields.filter((f) => f.group === currentGroupId);

  // Обработчик изменения значения поля
  const handleChange = (key: string, value: string) => {
    setFormData({ ...formData, [key]: value });
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  // Обработчик для textarea с автоподстройкой высоты
  const handleTextareaChange = (key: string, value: string) => {
    setFormData({ ...formData, [key]: value });
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Функция для рендера поля в зависимости от его типа
  const renderField = (field: ModuleSchemaField) => {
    const errorMsg = errors[field.key] || null;
    switch (field.field_type) {
      case "date":
        return (
          <FieldWrapper key={field.key}>
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
            <DatePickerInput
              value={formData[field.key] || ""}
              placeholder={field.question || field.key}
              onChange={(value) => handleChange(field.key, value)}
            />
          </FieldWrapper>
        );
      case "textarea":
        return (
          <FieldWrapper key={field.key}>
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
            <TitleTooltip text={field.question || ""}>
            <Input
              as="textarea"
              ref={textareaRef}
              placeholder={field.question || field.key}
              value={formData[field.key] || ""}
              onChange={(e) => handleTextareaChange(field.key, e.target.value)}
              hasError={!!errorMsg}
            />
            </TitleTooltip>
          </FieldWrapper>
        );
      case "select":
      case "dropdown":
        return (
          <FieldWrapper key={field.key}>
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
            <DropdownWithInput
              options={
                field.answer_options
                  ? field.answer_options.map((opt) => opt.value)
                  : []
              }
              placeholder={field.question || "Выберите значение"}
              value={formData[field.key] || ""}
              onChange={(value) => handleChange(field.key, value)}
            />
          </FieldWrapper>
        );
      default:
        // Если тип поля не задан – используем "text" по умолчанию
        const inputType = field.field_type || "text";
        console.log(inputType);
        return (
          <FieldWrapper key={field.key}>
            {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}
            <TitleTooltip text={field.question || ""}>
            <Input
              type={inputType}
              placeholder={field.question || field.key}
              value={formData[field.key] || ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              hasError={!!errorMsg}
            />
            </TitleTooltip>
          </FieldWrapper>
        );
    }
  };

  // Проверка валидации для текущей группы полей
  const areCurrentGroupFieldsValid = currentGroupFields.every((field) => {
    if (!field.required) return true;
    const value = formData[field.key]?.trim() ?? "";
    return value !== "";
  });

  // Обработчик клика по кнопке "Подтвердить"/"Сохранить"
  const handleButtonClick = () => {
    const newErrors: Record<string, string | null> = {};
    currentGroupFields.forEach((field) => {
      if (field.required && !formData[field.key]?.trim()) {
        newErrors[field.key] = "Это поле обязательно";
      }
    });

    setErrors(newErrors);

    const isValid = Object.values(newErrors).every((e) => e === null);
    if (!isValid) return;

    if (currentGroupIndex < extendedGroups.length - 1) {
      setCurrentGroupIndex(currentGroupIndex + 1);
    } else {
      onSubmit(formData);
    }
  };

  const isLastGroup = currentGroupIndex === extendedGroups.length - 1;
  const buttonLabel = isLastGroup ? submitButtonText : "Подтвердить";

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <h2>{title}</h2>
          <CloseButton onClick={onCancel}>
            <img src={closeIcon} alt="Закрыть" />
          </CloseButton>
        </ModalHeader>
        <Divider />
        <ModalBody>
          <form onSubmit={(e) => e.preventDefault()}>
            {currentGroupFields.map((field) => renderField(field))}
          </form>
        </ModalBody>
        <ModalFooter>
          <ButtonWrapper>
            <Button
              type="button"
              disabled={!areCurrentGroupFieldsValid}
              width="100%"
              onClick={handleButtonClick}
            >
              {buttonLabel}
            </Button>
          </ButtonWrapper>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ModuleModal;
